import type { AssignmentSuggestion } from '@chukua/common';
import { Button } from 'antd';
import { TriangleAlert } from 'lucide-react';
import clsx from 'clsx';
import { trailerAvailabilityLabel } from '../policies/trailer-availability.policy';
import { Registration } from './Registration';

interface CrewRowProps {
  crew: AssignmentSuggestion;
  isActive: boolean;
  isChoosing: boolean;
  isChoiceLocked: boolean;
  onChoose: (crew: AssignmentSuggestion) => void;
}

/** One ranked crew on a single dense line: rank, rig, driver, the one-line reason (and warnings), Choose. */
export function CrewRow({ crew, isActive, isChoosing, isChoiceLocked, onChoose }: CrewRowProps) {
  const { truck, trailer, driver, warnings } = crew;
  const availability = trailerAvailabilityLabel(trailer.availability);
  return (
    <li
      id={`crew-${crew.rank}`}
      aria-current={isActive ? 'true' : undefined}
      className={clsx('flex items-center gap-3 border-b border-line px-3 py-2 last:border-b-0', isActive && 'bg-primary/10')}
    >
      <span className="w-8 shrink-0 text-sm font-semibold text-fg">#{crew.rank}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-x-2 text-sm">
          <Registration value={truck.registration} linksToUnit />
          <span className="text-muted">+</span>
          <Registration value={trailer.registration} />
          <span className={clsx('text-xs', availability.needsSwap ? 'text-warning' : 'text-muted')}>({availability.text})</span>
          <span className="text-fg">{driver.name}</span>
        </div>
        <span className="text-xs text-muted">{crew.reason}</span>
        {warnings.map((warning) => (
          <span key={warning} className="flex items-center gap-1 text-xs text-warning">
            <TriangleAlert className="size-3" aria-hidden />
            {warning}
          </span>
        ))}
      </div>
      <Button type="primary" size="small" loading={isChoosing} disabled={isChoiceLocked} onClick={() => onChoose(crew)}>
        Choose
      </Button>
    </li>
  );
}
